import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { api } from '../config/api';
import {
  Home,
  BarChart3,
  Calendar,
  FolderLock,
  Users,
  Video,
  Building2,
  FileText,
  Edit3,
  PenTool,
  Search,
  CheckCircle,
  DollarSign,
  ClipboardCheck,
  Plus,
  Upload,
  Eye,
  Settings,
  HelpCircle,
  Shield,
  TrendingUp,
  AlertTriangle,
  GraduationCap,
  GitBranch,
  LayoutDashboard,
  Target,
  Briefcase,
  Flag,
  Plug,
  Scale,
  Gauge,
  Timer,
  ListTodo,
  UserCheck,
  MessageSquare,
  FileSearch,
  FolderOpen,
  ClipboardList
} from 'lucide-react';
import logger from '../utils/logger';

// Comprehensive command list with Lucide icons
const commands = [
  // Core Navigation
  { id: 'dashboard', label: 'Go to Dashboard', icon: Home, category: 'Navigation', action: '/' },
  { id: 'portfolio', label: 'Portfolio Radar', icon: BarChart3, category: 'Navigation', action: '/portfolio' },
  { id: 'calendar', label: 'Calendar & Deadlines', icon: Calendar, category: 'Navigation', action: '/calendar' },
  { id: 'vault', label: 'Workspace Files', icon: FolderLock, category: 'Navigation', action: '/workspace-files' },
  { id: 'team', label: 'Team Workspace', icon: Users, category: 'Navigation', action: '/team-workspace' },
  { id: 'meeting', label: 'Meeting Room', icon: Video, category: 'Navigation', action: '/collaboration-room' },
  { id: 'vendors', label: 'Vendor Database', icon: Building2, category: 'Navigation', action: '/vendor-database' },
  { id: 'solicitations', label: 'Solicitation Hub', icon: FileText, category: 'Navigation', action: '/solicitation-hub' },
  { id: 'opportunities', label: 'Opportunities List', icon: Target, category: 'Navigation', action: '/opportunities' },
  
  // AI Tools
  { id: 'document-creator', label: 'Document Creator', icon: PenTool, category: 'AI Tools', action: '/document-creator' },
  { id: 'contract-analyzer', label: 'AI Contract Analyzer', icon: FileSearch, category: 'AI Tools', action: '/contract-analyzer' },
  { id: 'compliance', label: 'AI Compliance Checker', icon: CheckCircle, category: 'AI Tools', action: '/compliance-checker' },
  { id: 'igce', label: 'IGCE Generator', icon: DollarSign, category: 'AI Tools', action: '/igce-generator' },
  { id: 'proposal', label: 'Proposal Evaluator', icon: ClipboardCheck, category: 'AI Tools', action: '/proposal-evaluator' },
  { id: 'clause-recommender', label: 'Clause Recommender', icon: Scale, category: 'AI Tools', action: '/clause-recommender' },
  { id: 'decision-wizard', label: 'Decision Wizard', icon: FileText, category: 'AI Tools', action: '/decision-wizard' },
  
  // Analytics & Insights
  { id: 'analytics', label: 'Analytics Dashboard', icon: BarChart3, category: 'Insights', action: '/analytics' },
  { id: 'leadership', label: 'Leadership Analytics', icon: TrendingUp, category: 'Insights', action: '/leadership-analytics' },
  { id: 'risk-dashboard', label: 'Risk Dashboard', icon: AlertTriangle, category: 'Insights', action: '/risk-dashboard' },
  { id: 'audit-dashboard', label: 'Audit Dashboard', icon: Shield, category: 'Insights', action: '/audit-dashboard' },
  { id: 'audit-log', label: 'User Audit Log', icon: Shield, category: 'Insights', action: '/audit-log' },
  { id: 'quick-wins', label: 'Quick Wins', icon: FileText, category: 'Insights', action: '/quick-wins' },
  
  // Project Management
  { id: 'projects', label: 'Projects', icon: Briefcase, category: 'Projects', action: '/projects' },
  { id: 'contracts', label: 'Contracts', icon: FileText, category: 'Projects', action: '/contracts' },
  { id: 'milestones', label: 'Milestones', icon: Flag, category: 'Projects', action: '/milestones' },
  { id: 'risks', label: 'Risks', icon: AlertTriangle, category: 'Projects', action: '/risks' },
  { id: 'pipeline', label: 'Acquisition Pipeline', icon: GitBranch, category: 'Projects', action: '/acquisition-pipeline' },
  { id: 'timeline', label: 'Acquisition Timeline', icon: Timer, category: 'Projects', action: '/acquisition-timeline' },
  
  // Government & Compliance
  { id: 'gov-integrations', label: 'Gov Integrations (SAM.gov)', icon: Plug, category: 'Government', action: '/gov-integrations' },
  { id: 'sdvosb', label: 'SDVOSB Tracker', icon: UserCheck, category: 'Government', action: '/sdvosb' },
  { id: 'market-research', label: 'Market Research', icon: Search, category: 'Government', action: '/market-research' },
  
  // Quick Actions
  // 'new-rfi' removed - legacy feature deleted
  { id: 'upload', label: 'Upload Document', icon: Upload, category: 'Quick Actions', action: '/workspace-files?upload=true' },
  { id: 'search-opps', label: 'Search Opportunities', icon: Eye, category: 'Quick Actions', action: '/opportunities' },
  { id: 'new-meeting', label: 'Schedule Meeting', icon: Calendar, category: 'Quick Actions', action: '/calendar?new=true' },
  
  // Learning & Support
  { id: 'learning', label: 'AI Learning Center', icon: GraduationCap, category: 'Learning', action: '/ai-learning-center' },
  { id: 'meeting-notes', label: 'Meeting Notes', icon: MessageSquare, category: 'Learning', action: '/meeting-notes' },
  
  // System
  { id: 'settings', label: 'Settings', icon: Settings, category: 'System', action: '/settings' },
  { id: 'help', label: 'Help Center', icon: HelpCircle, category: 'System', action: '/help' },
  { id: 'integrations', label: 'Integrations', icon: Plug, category: 'System', action: '/integrations' },
  { id: 'admin', label: 'Admin Panel', icon: Shield, category: 'System', action: '/admin' },
];

function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('commands');
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // Filter commands
  const filteredCommands = query
    ? commands.filter(cmd => 
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.category.toLowerCase().includes(query.toLowerCase())
      )
    : commands;

  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  const flatCommands = Object.values(groupedCommands).flat();

  // Search API
  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await api.get(`/search/global?q=${encodeURIComponent(searchQuery)}&limit=10`);
      setSearchResults(results);
      if (results.total > 0) {
        setActiveTab('results');
      }
    } catch (error) {
      logger.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setSearchResults(null);
      setActiveTab('commands');
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
      setSearchResults(null);
      setActiveTab('commands');
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.querySelector('[data-selected="true"]');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  const handleSelect = (cmd) => {
    onClose();
    if (cmd.url) {
      navigate(cmd.url);
    } else if (cmd.action) {
      navigate(cmd.action);
    }
  };

  // Get all selectable items
  const getAllItems = useCallback(() => {
    if (activeTab === 'results' && searchResults) {
      const items = [];
      ['documents', 'vendors', 'solicitations', 'calendar', 'team_files'].forEach(type => {
        searchResults[type]?.forEach(item => items.push({ ...item, resultType: type }));
      });
      return items;
    }
    return flatCommands;
  }, [activeTab, searchResults, flatCommands]);

  const allItems = getAllItems();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && allItems[selectedIndex]) {
        e.preventDefault();
        handleSelect(allItems[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        if (searchResults && searchResults.total > 0) {
          setActiveTab(prev => prev === 'commands' ? 'results' : 'commands');
          setSelectedIndex(0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedIndex, allItems, onClose]);

  if (!isOpen) return null;

  const getResultIcon = (type) => {
    const icons = {
      document: FileText,
      vendor: Building2,
      solicitation: ClipboardList,
      event: Calendar,
      team_file: FolderOpen
    };
    return icons[type] || FileText;
  };

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]"
      data-testid="command-palette-overlay"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="command-palette-backdrop"
      />
      
      {/* Palette */}
      <div 
        className={`relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}
        style={{ animation: 'commandPaletteIn 0.2s ease-out' }}
        data-testid="command-palette-container"
        role="dialog"
        aria-modal="true"
        aria-label="Command Palette"
      >
        {/* Search Input */}
        <div className={`flex items-center gap-3 px-4 py-4 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <Search className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            placeholder="Search documents, vendors, commands..."
            className={`flex-1 bg-transparent text-lg outline-none ${
              darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
            }`}
            data-testid="command-palette-input"
            aria-label="Search commands and content"
          />
          {isSearching && (
            <div 
              className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
              data-testid="command-palette-loading"
            />
          )}
          <kbd className={`px-2 py-1 text-xs font-mono rounded ${
            darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
          }`}>ESC</kbd>
        </div>

        {/* Tabs when search results exist */}
        {searchResults && searchResults.total > 0 && (
          <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => { setActiveTab('commands'); setSelectedIndex(0); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'commands'
                  ? (darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600')
                  : (darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
              }`}
              data-testid="command-palette-tab-commands"
            >
              Commands ({filteredCommands.length})
            </button>
            <button
              onClick={() => { setActiveTab('results'); setSelectedIndex(0); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'results'
                  ? (darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600')
                  : (darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
              }`}
              data-testid="command-palette-tab-results"
            >
              Results ({searchResults.total})
            </button>
          </div>
        )}

        {/* Results */}
        <div 
          className="max-h-96 overflow-y-auto py-2"
          ref={resultsContainerRef}
          data-testid="command-palette-results"
        >
          {activeTab === 'commands' ? (
            // Commands view
            filteredCommands.length > 0 ? (
              Object.entries(groupedCommands).map(([category, cmds]) => (
                <div key={category}>
                  <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                    darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {category}
                  </div>
                  {cmds.map((cmd) => {
                    const globalIndex = flatCommands.findIndex(c => c.id === cmd.id);
                    const isSelected = globalIndex === selectedIndex;
                    const IconComponent = cmd.icon;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => handleSelect(cmd)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        data-selected={isSelected}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                          isSelected
                            ? (darkMode ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-50 text-blue-700')
                            : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
                        }`}
                        data-testid={`command-palette-item-${cmd.id}`}
                      >
                        <IconComponent className="w-5 h-5 opacity-70" />
                        <span className="font-medium">{cmd.label}</span>
                        {isSelected && (
                          <span className={`ml-auto text-xs ${
                            darkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>↵ to select</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No commands match "{query}"</p>
                <p className="text-sm mt-1">Try searching for documents or vendors</p>
              </div>
            )
          ) : (
            // Search results view
            searchResults && (
              <>
                {['documents', 'vendors', 'solicitations', 'calendar', 'team_files'].map(type => {
                  const items = searchResults[type] || [];
                  if (items.length === 0) return null;
                  
                  const typeLabels = {
                    documents: 'VA Library',
                    vendors: 'Vendors',
                    solicitations: 'Solicitations',
                    calendar: 'Calendar Events',
                    team_files: 'Team Files'
                  };
                  
                  return (
                    <div key={type}>
                      <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                        darkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {typeLabels[type]} ({items.length})
                      </div>
                      {items.map((item, idx) => {
                        const currentItems = getAllItems();
                        const globalIdx = currentItems.findIndex(i => i.id === item.id && i.resultType === type);
                        const isSelected = globalIdx === selectedIndex;
                        const IconComponent = getResultIcon(item.type);
                        return (
                          <button
                            key={`${type}-${item.id}`}
                            onClick={() => handleSelect(item)}
                            onMouseEnter={() => setSelectedIndex(globalIdx)}
                            data-selected={isSelected}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                              isSelected
                                ? (darkMode ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-50 text-blue-700')
                                : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
                            }`}
                            data-testid={`command-palette-result-${type}-${item.id}`}
                          >
                            <IconComponent className="w-5 h-5 opacity-70" />
                            <div className="flex-1 text-left min-w-0">
                              <div className="font-medium truncate">{item.title}</div>
                              {item.preview && (
                                <div className={`text-sm truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {item.preview}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>↵</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )
          )}
          
          {/* No results message */}
          {query.length >= 2 && !isSearching && filteredCommands.length === 0 && (!searchResults || searchResults.total === 0) && (
            <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No results found for "{query}"</p>
              <p className="text-sm mt-1">Try different keywords or check your spelling</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 py-2.5 border-t flex items-center justify-between text-xs ${
          darkMode ? 'border-gray-700 text-gray-500 bg-gray-800/50' : 'border-gray-200 text-gray-400 bg-gray-50/50'
        }`}>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>↑</kbd>
              <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>↓</kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>↵</kbd>
              <span className="ml-1">Select</span>
            </span>
            {searchResults && searchResults.total > 0 && (
              <span className="flex items-center gap-1">
                <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>Tab</kbd>
                <span className="ml-1">Switch tabs</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>⌘</kbd>
            <kbd className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>K</kbd>
            <span className="ml-1">to open</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes commandPaletteIn {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default CommandPalette;
